import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../../layout/Header/Header";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CapturaMateriales, { renglonVacio, renglonesParaEnviar, telaSinColor } from "../../Materiales/CapturaMateriales";
import { limiteDisponible, coloresConExistencia, materialesConExistencia, saldoDe } from "../../Materiales/existencias";
import { v4 as uuidv4 } from "uuid";

/**
 * Apartar tela para un pedido, por CANTIDAD (ya no por rollo).
 *
 * No mueve inventario: lo apartado deja de estar disponible para otras
 * salidas, y se consume cuando se le manda tela al maquilero de ese pedido.
 */
function ApartadosCreate() {
  const { user } = useContext(AuthContext);
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_TELAS = process.env.REACT_APP_URL_TELAS;
  const URL_COLORES = process.env.REACT_APP_URL_COLORES;
  const URL_INVENTARIOS_MATERIALES = process.env.REACT_APP_URL_INVENTARIOS_MATERIALES;
  const URL_APARTADOS_MATERIALES = process.env.REACT_APP_URL_APARTADOS_MATERIALES;
  const URL_REQUISICION = process.env.REACT_APP_URL_REQUISICION;

  const [pedidos, setPedidos] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("");
  const [telas, setTelas] = useState([]);
  const [colores, setColores] = useState([]);
  const [existencias, setExistencias] = useState([]);
  const [renglones, setRenglones] = useState([renglonVacio("Telas")]);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cab = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  useEffect(() => {
    axios.get(URL_PEDIDOS, cab())
      .then((r) => setPedidos(r.data.filter((p) => p.is_active !== "No").sort((a, b) => b.idPedido - a.idPedido)))
      .catch((e) => console.log(e));
    axios.get(URL_AREAS, cab()).then((r) => {
      const bodegas = r.data.filter((a) => (a.tipo || "Bodega") === "Bodega");
      setAreas(bodegas);
      const principal = bodegas.find((a) => a.name === "Almacen") || bodegas[0];
      if (principal) setArea(principal._id);
    });
    axios.get(URL_TELAS, cab()).then((r) => setTelas(r.data)).catch((e) => console.log(e));
    axios.get(URL_COLORES, cab()).then((r) => setColores(r.data)).catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    if (!area) return;
    let vigente = true; // si cambian de almacen, la respuesta anterior ya no vale
    axios.get(`${URL_INVENTARIOS_MATERIALES}?areas=${area}`, cab()).then((r) => vigente && setExistencias(r.data)).catch((e) => console.log(e));
    return () => { vigente = false; };
  }, [area]);

  // Lo que pide la explosion del pedido y aun no se le manda al maquilero,
  // hasta lo que haya disponible.
  function prellenar() {
    if (!pedido) return Swal.fire({ icon: "warning", title: "Elige el pedido" });
    Promise.all([
      axios.get(`${URL_REQUISICION}Pedidos?pedidos=${pedido._id}&tipoMaterial=Telas`, cab()),
      axios.get(`${URL_APARTADOS_MATERIALES}?pedidos=${pedido._id}&soloPendientes=Si`, cab()),
    ])
      .then(([r, ap]) => {
        // Lo que este pedido ya tiene apartado no se vuelve a proponer.
        const yaApartado = new Map();
        for (const a of ap.data) {
          const k = `${a.material?._id || a.material}|${a.colores?._id || a.colores || ""}`;
          yaApartado.set(k, (yaApartado.get(k) || 0) + a.pendiente);
        }
        const filas = r.data.renglones
          .map((x) => ({ ...x, porApartar: x.porEnviar - (yaApartado.get(`${x.material}|${x.colores || ""}`) || 0) }))
          .filter((x) => x.porApartar > 0)
          .map((x) => {
            const base = { id: uuidv4(), tipoMaterial: "Telas", material: x.material, colores: x.colores || "" };
            const s = saldoDe(existencias, base);
            const disponible = s ? s.disponible ?? s.cantidad : 0;
            return { ...base, cantidad: Math.round(Math.max(0, Math.min(x.porApartar, disponible)) * 100) / 100 };
          })
          .filter((x) => x.cantidad > 0);
        if (!filas.length) {
          return Swal.fire({ icon: "info", title: "Nada que apartar", text: "El pedido no tiene explosion de tela pendiente, o no hay tela disponible." });
        }
        setRenglones(filas);
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo leer el pedido", text: e.response?.data?.message || e.message }));
  }

  async function guardar() {
    if (!pedido) return Swal.fire({ icon: "warning", title: "Elige el pedido" });
    if (telaSinColor(renglones)) return Swal.fire({ icon: "warning", title: "Falta el color de una tela" });
    const envio = renglonesParaEnviar(renglones);
    if (!envio.length) return Swal.fire({ icon: "warning", title: "Captura al menos una tela con cantidad" });
    setGuardando(true);
    try {
      const { data } = await axios.post(URL_APARTADOS_MATERIALES, {
        pedidos: pedido._id, fecha, observaciones, areas: area, renglones: envio,
      }, cab());
      await Swal.fire({ icon: "success", title: `Apartado ${data.idApartado} registrado` });
      window.location.href = "/ListadoApartados";
    } catch (error) {
      const info = error.response?.data || {};
      Swal.fire({ icon: "error", title: "No se pudo apartar", text: info.message || error.message });
      setGuardando(false);
    }
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion ? (
        <div className="card container col-10">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-box-open" style={{ marginRight: 8, color: "#8b5cf6" }} />
                Apartar Tela
              </h3>
              <p className="page-subtitle">Aparta metros de tela para un pedido. Sin rollos ni QR.</p>
            </div>
          </div>
          <Row>
            <Col md={5}>
              <Label>Pedido</Label>
              <Autocomplete
                size="small"
                options={pedidos}
                value={pedido}
                onChange={(e, v) => setPedido(v)}
                getOptionLabel={(p) => `${p.idPedido} ${p.clientes?.[0]?.nombre_comercial ?? ""} ${p.pedidoCliente ?? ""}`.trim()}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca por numero o cliente" />}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label>Almacen</Label>
              <Input type="select" value={area} onChange={(e) => setArea(e.target.value)}>
                {areas.map((a) => (
                  <option value={a._id} key={a._id}>{a.name}</option>
                ))}
              </Input>
            </Col>
            <Col md={3} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button color="info" onClick={prellenar}>
                <i className="fas fa-magic" style={{ marginRight: 6 }} /> Prellenar con el pedido
              </Button>
            </Col>
          </Row>
          <Row style={{ marginTop: 8 }}>
            <Col md={12}>
              <Label>Observaciones</Label>
              <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
            </Col>
          </Row>
          <br />
          <CapturaMateriales
            renglones={renglones}
            setRenglones={setRenglones}
            telas={telas}
            habilitacion={[]}
            colores={colores}
            soloTipo="Telas"
            opcionesDe={() => materialesConExistencia(existencias, telas, "Telas")}
            coloresDe={coloresConExistencia(existencias, colores)}
            limiteDe={limiteDisponible(existencias)}
          />
          <div className="submit-bar">
            <Button href="/ListadoApartados" className="btn btn-danger">Regresar</Button>
            <Button className="btn btn-success" onClick={guardar} disabled={guardando}>
              <i className="fas fa-save" style={{ marginRight: 6 }} /> Apartar
            </Button>
          </div>
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default ApartadosCreate;
